import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "../components/SEO";
import { blogPosts } from "../mockData";
import { useAudio } from "../context/AudioContext";

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* 🎧 AUDIO CONTEXT */
  const { pauseMini, setActivePlayer, currentPlayer } = useAudio();

  /* 🎬 refs vidéos */
  const localVideoRef = useRef(null);
  const youtubeIframeRef = useRef(null);

  const projectSwipeRef = useRef(null);
  const projectWheelRef = useRef(0);
  const projectWheelTimerRef = useRef(null);
  const projectWheelGestureLockRef = useRef(false);
  const projectNavLockRef = useRef(false);

  useEffect(() => {
    const foundPost = blogPosts.find(
      (p) => p.slug === slug && p.published
    );

    if (foundPost) {
      setPost(foundPost);
      setError(null);
    } else {
      setError("Article non trouvé");
    }

    setLoading(false);
  }, [slug]);

  const publishedPosts = blogPosts.filter((item) => item.published);

  const currentProjectIndex = publishedPosts.findIndex(
    (item) => item.slug === slug
  );

  const goToProject = (direction) => {
    if (
      projectNavLockRef.current ||
      currentProjectIndex < 0 ||
      publishedPosts.length < 2
    ) {
      return;
    }

    projectNavLockRef.current = true;

    const nextIndex =
      (currentProjectIndex + direction + publishedPosts.length) %
      publishedPosts.length;

    const nextPost = publishedPosts[nextIndex];

    pauseMini();
    setActivePlayer("navigation");

    navigate(`/actualites/${nextPost.slug}`);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    window.setTimeout(() => {
      projectNavLockRef.current = false;
    }, 500);
  };

  /* SWIPE MOBILE */
  const handleProjectTouchStart = (event) => {
    if (event.touches.length !== 1) return;

    if (
      event.target.closest?.(
        "button, a, iframe, input, textarea, select"
      )
    ) {
      return;
    }

    const touch = event.touches[0];

    projectSwipeRef.current = {
      x: touch.clientX,
      y: touch.clientY,
    };
  };

  const handleProjectTouchEnd = (event) => {
    const start = projectSwipeRef.current;

    projectSwipeRef.current = null;

    if (!start || event.changedTouches.length !== 1) return;

    const touch = event.changedTouches[0];

    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;

    if (
      Math.abs(dx) < 60 ||
      Math.abs(dx) <= Math.abs(dy) * 1.15
    ) {
      return;
    }

    goToProject(dx < 0 ? 1 : -1);
  };

  /* ESCAPE → RETOUR ACTUALITÉS */
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        navigate("/actualites");
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [navigate]);

  /* 🔁 MINI-LECTEUR → COUPE LA VIDÉO */
  useEffect(() => {
    if (currentPlayer === "navigation") {
      try {
        localVideoRef.current?.pause();
      } catch {}

      youtubeIframeRef.current?.contentWindow?.postMessage(
        JSON.stringify({
          event: "command",
          func: "pauseVideo",
          args: [],
        }),
        "*"
      );
    }
  }, [currentPlayer]);

  const getVideoUrl = (url) => {
    if (!url) return "";

    if (url.includes("youtube.com/embed/")) {
      const sep = url.includes("?") ? "&" : "?";

      return `${url}${sep}enablejsapi=1&autoplay=0&rel=0`;
    }

    return url;
  };

  const renderVideoPlayer = () => {
    if (!post?.video_url) return null;

    /* 🎬 YOUTUBE */
    if (post.video_url.includes("youtube.com/embed/")) {
      return (
        <div className="mb-8">
          <div className="relative w-full pb-[56.25%] bg-black rounded-lg overflow-hidden border border-gray-800">
            <iframe
              ref={youtubeIframeRef}
              src={getVideoUrl(post.video_url)}
              title={`Vidéo: ${post.title}`}
              className="absolute top-0 left-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              onLoad={() => {
                pauseMini();
                setActivePlayer("external");
              }}
            />
          </div>
        </div>
      );
    }

    /* 🎬 VIDÉO LOCALE */
    return (
      <div className="mb-8">
        <div className="relative w-full bg-black rounded-lg overflow-hidden border border-gray-800">
          <video
            ref={localVideoRef}
            className="w-full"
            controls
            preload="metadata"
            onPlay={() => {
              pauseMini();
              setActivePlayer("external");
            }}
          >
            <source
              src={post.video_url}
              type="video/mp4"
            />

            Votre navigateur ne supporte pas la lecture de vidéos.
          </video>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FDD6E0]" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <div className="text-center">
          <div className="text-5xl mb-4">
            😕
          </div>

          <h1 className="text-2xl font-bold text-white mb-4">
            Article non trouvé
          </h1>

          <p className="text-gray-400 mb-6">
            L'article que vous recherchez n'existe pas ou a été supprimé.
          </p>

          <button
            onClick={() => navigate("/actualites")}
            className="text-gray-300 hover:text-[#FDD6E0] transition-colors"
          >
            ← Retour aux actualités
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`${post.title} - Charles Ludig`}
        description={post.excerpt}
        url={`https://www.charlesludig.com/actualites/${post.slug}`}
        image={post.image}
      />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="min-h-screen bg-black pt-20 pb-12"
        data-page-swipe-lock="true"
        onTouchStart={handleProjectTouchStart}
        onTouchEnd={handleProjectTouchEnd}
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            navigate("/actualites");
          }
        }}
      >

        {/* CROIX EN HAUT À DROITE */}
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            navigate("/actualites");
          }}
          className="fixed top-24 right-4 md:top-24 md:right-6 z-40 inline-flex items-center gap-2 rounded-full bg-black/85 backdrop-blur-md border border-white/20 px-3.5 py-2 text-sm text-white shadow-xl hover:bg-white hover:text-black transition"
          aria-label="Fermer et revenir aux actualités"
          title="Retour aux actualités"
        >
          <span className="text-lg leading-none">
            ×
          </span>

          <span className="hidden sm:inline">
            Actualités
          </span>
        </button>

        {/* FLÈCHES DESKTOP */}
        {publishedPosts.length > 1 && (
          <>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                goToProject(-1);
              }}
              className="hidden md:flex fixed left-5 top-1/2 -translate-y-1/2 z-30 w-11 h-11 rounded-full bg-black/80 border border-[#FDD6E0]/40 text-[#FDD6E0] text-2xl items-center justify-center hover:bg-[#FDD6E0]/10"
              aria-label="Projet précédent"
            >
              ‹
            </button>

            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                goToProject(1);
              }}
              className="hidden md:flex fixed right-5 top-1/2 -translate-y-1/2 z-30 w-11 h-11 rounded-full bg-black/80 border border-[#FDD6E0]/40 text-[#FDD6E0] text-2xl items-center justify-center hover:bg-[#FDD6E0]/10"
              aria-label="Projet suivant"
            >
              ›
            </button>
          </>
        )}

        <div
          className="container mx-auto px-4 sm:px-6 max-w-4xl"
          onClick={(event) => event.stopPropagation()}
        >

          {/* RETOUR EN HAUT */}
          <button
            onClick={() => navigate("/actualites")}
            className="mt-4 md:mt-6 mb-6 text-gray-300 hover:text-[#FDD6E0] transition-colors flex items-center gap-2"
          >
            ← Retour aux actualités
          </button>

          {/* ARTICLE */}
          <article className="bg-gray-900 rounded-lg overflow-hidden border border-gray-800">

            {post.image && (
              <div className="relative overflow-hidden">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-0 md:h-0 object-cover"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent opacity-60" />
              </div>
            )}

            <div className="p-5 md:p-10">

              {/* DATE */}
              <div className="flex items-center justify-between mb-4 md:mb-6 text-sm text-gray-400">
                <span className="text-[#FDD6E0]">
                  {new Date(post.created_at).toLocaleDateString(
                    "fr-FR",
                    {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    }
                  )}
                </span>
              </div>

              {/* TITRE */}
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6 leading-tight">
                {post.title}
              </h1>

              {/* DESCRIPTION */}
              <p className="text-sm sm:text-base md:text-xl text-gray-300 mb-6 md:mb-8 leading-relaxed">
                {post.excerpt}
              </p>

              {/* SÉPARATEUR */}
              <div
                className="h-px mb-8"
                style={{
                  background:
                    "linear-gradient(to right, transparent, #FDD6E0, transparent)",
                }}
              />

              {/* VIDÉO */}
              {renderVideoPlayer()}

              {/* TEXTE */}
              <div className="prose prose-invert prose-pink max-w-none">
                {post.content
                  .split("\n")
                  .map((paragraph, index) =>
                    paragraph.trim() ? (
                      <p
                        key={index}
                        className="text-gray-300 leading-relaxed mb-4 text-base md:text-lg"
                      >
                        {paragraph}
                      </p>
                    ) : null
                  )}
              </div>

            </div>
          </article>

          {/* NAVIGATION PROJET PRÉCÉDENT / SUIVANT */}
          {publishedPosts.length > 1 && (
            <div className="flex items-center justify-between gap-3 mt-8 text-sm">

              <button
                type="button"
                onClick={() => goToProject(-1)}
                className="text-[#FDD6E0] hover:text-white transition-colors"
              >
                ← Projet précédent
              </button>

              <span aria-hidden="true" />

              <button
                type="button"
                onClick={() => goToProject(1)}
                className="text-[#FDD6E0] hover:text-white transition-colors"
              >
                Projet suivant →
              </button>

            </div>
          )}

        </div>
      </motion.div>
    </>
  );
};

export default BlogPost;